import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  registerTableMenu,
  unregisterTableMenu,
} from "src/charts/tableMenuRegistry";
import {
  downloadKpiCsv,
  downloadKpiPng,
  downloadKpiPdf,
  downloadNodePng,
  downloadNodePdf,
  type KpiExportData,
} from "src/helpers/widgetExport";
import "./KpiExportMenu.scss";

const MENU_WIDTH = 190;

type Props = {
  data: KpiExportData;
  title?: string;
  // Widget description from the dashboard detail API — included in the CSV.
  description?: string | null;
  // The widget root to snapshot for PNG/PDF. The menu prefers the enclosing
  // dashboard card (includes the title header) when one exists.
  targetRef: React.RefObject<HTMLElement | null>;
  // The card-header hamburger (WidgetRenderer → openTableMenu) is the only
  // trigger: this menu registers under the widget id used as container id.
  menuId?: string;
  // A fixed-size export view for image/PDF, rendered off-screen so the output
  // is crisp and full-size regardless of the on-screen tile size. Falls back
  // to snapshotting the live card when omitted.
  renderExport?: () => ReactNode;
};

export default function KpiExportMenu({
  data,
  title,
  description,
  targetRef,
  menuId,
  renderExport,
}: Props) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState<"png" | "pdf" | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openAt = useCallback((pos: { x: number; y: number }) => {
    setMenuPos({
      x: Math.min(
        Math.max(pos.x - MENU_WIDTH, 8),
        window.innerWidth - MENU_WIDTH - 8,
      ),
      y: Math.min(pos.y + 6, window.innerHeight - 220),
    });
  }, []);

  const close = useCallback(() => setMenuPos(null), []);

  // Let the dashboard card-header hamburger open this menu.
  useEffect(() => {
    if (!menuId) return;
    registerTableMenu(menuId, openAt);
    return () => unregisterTableMenu(menuId);
  }, [menuId, openAt]);

  // Outside click / Esc close.
  useEffect(() => {
    if (!menuPos) return;
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuPos, close]);

  const snapshotTarget = (): HTMLElement | null => {
    const el = targetRef.current;
    if (!el) return null;
    return (el.closest(".card") as HTMLElement | null) ?? el;
  };

  const run = async (kind: "png" | "pdf" | "csv") => {
    try {
      if (kind === "csv") {
        downloadKpiCsv(data, title, description);
      } else {
        setBusy(kind);
        // Preferred: render the dedicated fixed-size export view off-screen so
        // the image is crisp and full-size no matter how small the tile is.
        const node = renderExport?.();
        if (node) {
          if (kind === "png") await downloadNodePng(node, data, title);
          else await downloadNodePdf(node, data, title);
        } else {
          const el = snapshotTarget();
          if (!el) return;
          if (kind === "png") await downloadKpiPng(el, data, title);
          else await downloadKpiPdf(el, data, title);
        }
      }
    } finally {
      setBusy(null);
      close();
    }
  };

  const item = (kind: "png" | "pdf" | "csv", icon: string, label: string) => (
    <button
      type="button"
      className="kpi-export__item"
      disabled={busy !== null}
      onClick={() => run(kind)}
    >
      <i
        className={`bi ${busy === kind ? "bi-hourglass-split" : icon}`}
        aria-hidden="true"
      />
      {label}
    </button>
  );

  return (
    <>
      {menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="kpi-export__menu"
            role="menu"
            style={{ left: menuPos.x, top: menuPos.y, width: MENU_WIDTH }}
            data-html2canvas-ignore="true"
          >
            {item("png", "bi-image", "Download PNG image")}
            {item("pdf", "bi-file-earmark-pdf", "Download PDF")}
            <div className="kpi-export__divider" />
            {item("csv", "bi-filetype-csv", "Download CSV data")}
          </div>,
          document.body,
        )}
    </>
  );
}
